/**
 * Feb 2026 R293zq · Inline per-line catalog picker.
 *
 * Small combobox that sits next to the description input of a line row
 * in the Composer. Lets the user type a query, see matching items from
 * the central catalog, and hydrate the current row with one click
 * (name/description, rate, HSN/SAC, tax %, unit).
 *
 * Keeps keyboard nav simple: focus the trigger, type to search, click
 * (or ↵) on a result. Click-away or Esc closes the popover.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Package, Paperclip } from "lucide-react";

export default function InlineCatalogPicker({ ws, onPick, size = "sm" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const popRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced catalog search while the popover is open.
  useEffect(() => {
    if (!open || !ws) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { active_only: true };
        if (q.trim()) params.q = q.trim();
        const r = await api.get(`/workspaces/${ws}/items`, { params });
        if (!cancelled) {
          setRows((r.data || []).slice(0, 25));
          setActiveIdx(0);
        }
      } catch { if (!cancelled) setRows([]); }
      finally { if (!cancelled) setLoading(false); }
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, ws, q]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Focus the search input when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 10);
  }, [open]);

  const pick = useCallback((item) => {
    onPick?.(item);
    setOpen(false);
    setQ("");
  }, [onPick]);

  const onKey = (e) => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (rows[activeIdx]) pick(rows[activeIdx]);
    }
  };

  const btnSize = size === "lg"
    ? "h-9 w-9"
    : "h-7 w-7";

  return (
    <div className="relative inline-flex" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${btnSize} inline-flex items-center justify-center rounded border border-border bg-background hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors`}
        data-testid="line-catalog-trigger"
        title="Search the catalog and insert into this line"
        tabIndex={-1}
      >
        <Package className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          className="absolute z-50 top-full right-0 mt-1 w-[22rem] max-w-[90vw] rounded-md border border-border bg-popover shadow-lg"
          data-testid="line-catalog-popover"
        >
          <div className="p-2 border-b border-border">
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKey}
              placeholder="Type to search catalog…"
              className="w-full h-8 text-xs px-2 bg-background border border-border focus:border-primary focus:outline-none rounded"
              data-testid="line-catalog-search"
            />
          </div>
          <div className="max-h-72 overflow-auto">
            {loading && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                Searching…
              </div>
            )}
            {!loading && rows.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                {q.trim() ? "No matches." : "Start typing to see catalog items."}
              </div>
            )}
            {!loading && rows.map((r, idx) => {
              const csym = r.currency === "INR" ? "₹" : (r.currency || "") + " ";
              return (
                <button
                  key={r.id}
                  type="button"
                  onMouseEnter={() => setActiveIdx(idx)}
                  onClick={() => pick(r)}
                  className={"w-full text-left px-3 py-1.5 text-xs border-b border-border last:border-b-0 " +
                    (idx === activeIdx ? "bg-primary/10" : "hover:bg-muted/60")}
                  data-testid={`line-catalog-item-${r.id}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate flex items-center gap-1">
                        {r.name}
                        {Array.isArray(r.spec_attachments) && r.spec_attachments.length > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1 py-[1px] rounded bg-amber-100 text-amber-700 text-[9px] font-semibold"
                            title={`${r.spec_attachments.length} spec sheet(s) · auto-copied to this document`}
                          >
                            <Paperclip className="h-2.5 w-2.5" />{r.spec_attachments.length}
                          </span>
                        )}
                      </div>
                      {r.description && (
                        <div className="text-[10px] text-muted-foreground truncate">
                          {r.description}
                        </div>
                      )}
                    </div>
                    <div className="text-right text-[10px] font-mono tabular-nums shrink-0">
                      <div>{csym}{Number(r.rate || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
                      <div className="text-muted-foreground">
                        {r.hsn_sac || "—"} · {r.tax_pct ?? 0}%
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
